import { useState, useEffect } from 'react';
import { Loader2, Brain, TrendingUp, AlertTriangle, CheckCircle, Info, Save, Download, Edit2, X, Check } from 'lucide-react';
import exploreApi from '../../exploreApi';

function normalizeAnalysis(raw) {
  if (!raw) return null;
  const d = typeof raw === 'string' ? JSON.parse(raw) : { ...raw };

  // Normalize string → correct field name
  if (!d.data_overview && d.file_summary) d.data_overview = d.file_summary;
  if (!d.purpose && d.domain) d.purpose = d.domain;

  // Flatten nested objects/arrays into a flat list of non-empty strings.
  // Handles cases where the LLM returns {"short_term": ["r1"], "long_term": ["r2"]}
  // instead of a plain array, which Object.values() would turn into [["r1"], ["r2"]].
  const flattenToStrings = (v) => {
    if (!v) return [];
    if (typeof v === 'string') return v.trim() ? [v] : [];
    if (Array.isArray(v)) return v.flatMap(flattenToStrings);
    if (typeof v === 'object') return Object.values(v).flatMap(flattenToStrings);
    return [String(v)];
  };

  const ensureArray = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (typeof v === 'object') return Object.values(v).filter(Boolean);
    return [];
  };

  d.key_columns = ensureArray(d.key_columns);
  d.column_details = ensureArray(d.column_details);
  d.business_insights = flattenToStrings(d.business_insights);
  d.potential_use_cases = flattenToStrings(d.potential_use_cases);
  d.recommendations = flattenToStrings(d.recommendations);
  d.data_patterns = flattenToStrings(d.data_patterns);

  if (d.data_quality) {
    d.data_quality.main_issues = flattenToStrings(d.data_quality.main_issues);
    d.data_quality.recommendations = flattenToStrings(d.data_quality.recommendations);
  }
  if (d.data_relationships) {
    d.data_relationships.potential_keys = ensureArray(d.data_relationships.potential_keys);
    d.data_relationships.categorical_columns = ensureArray(d.data_relationships.categorical_columns);
    d.data_relationships.hierarchical_relationships = ensureArray(d.data_relationships.hierarchical_relationships);
  }
  return d;
}

const importanceBadge = (imp) => {
  const v = (imp || '').toLowerCase();
  if (v === 'high') return 'bg-red-100 text-red-700';
  if (v === 'medium') return 'bg-yellow-100 text-yellow-700';
  if (v === 'low') return 'bg-green-100 text-green-700';
  return 'bg-gray-100 text-gray-600';
};

export default function LLMAnalysis({ filePath, sourceId, objectPath, sheetName }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editState, setEditState] = useState(null);
  const [autoTriggered, setAutoTriggered] = useState(false);

  useEffect(() => {
    const loadSaved = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await exploreApi.getSavedAnalyses({ file_path: filePath, source_id: sourceId, object_path: objectPath });
        if (response.success && response.analyses?.length > 0) {
          const data = normalizeAnalysis(response.analyses[0].analysis_data);
          setAnalysis(data);
          setEditState(JSON.parse(JSON.stringify(data)));
        }
      } catch (err) {
        console.error('Failed to load saved analysis:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSaved();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, sourceId, objectPath, sheetName]);

  const runAnalysis = async () => {
    setIsGenerating(true);
    setLoading(true);
    setError(null);
    try {
      const response = await exploreApi.analyzeComprehensive({
        file_path: filePath || '',
        source_id: sourceId,
        object_path: objectPath,
        sheet_name: sheetName,
        force: true
      });
      if (response.success && response.data) {
        const data = normalizeAnalysis(response.data.analysis ?? response.data);
        setAnalysis(data);
        setEditState(JSON.parse(JSON.stringify(data)));
        setUsage(response.llm_usage || null);
        try {
          await exploreApi.saveAnalysis({ file_path: filePath, source_id: sourceId, object_path: objectPath, sheet_name: sheetName, analysis_data: data });
        } catch { /* silent */ }
      } else {
        setError(response.error || 'Analysis failed');
      }
    } catch (err) {
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!loading && !analysis && !isGenerating && !error && !autoTriggered) {
      setAutoTriggered(true);
      runAnalysis();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, analysis, isGenerating, error, autoTriggered]);

  const handleSave = async () => {
    if (!editState) return;
    setSaving(true);
    try {
      const response = await exploreApi.saveAnalysis({ file_path: filePath, source_id: sourceId, object_path: objectPath, sheet_name: sheetName, analysis_data: editState });
      if (response.success) {
        setAnalysis(editState);
        setSaveSuccess(true);
        setEditMode(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else throw new Error('Save failed');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!analysis) return;
    const blob = new Blob([JSON.stringify(analysis, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !analysis) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-gray-600 font-medium">{isGenerating ? 'Generating AI analysis…' : 'Loading…'}</p>
        <p className="text-xs text-gray-400 mt-1">This may take up to 3 minutes</p>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-gray-600 font-medium">Generating AI analysis…</p>
        <p className="text-xs text-gray-400 mt-1">This may take up to 3 minutes</p>
      </div>
    );
  }

  if (error && !analysis) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-800">Analysis failed</p>
          <p className="text-xs text-red-600 mt-1">{error}</p>
          <button onClick={runAnalysis} className="mt-2 text-xs text-red-700 underline">Retry</button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="text-center py-12">
        <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium mb-4">No analysis yet</p>
        <button onClick={runAnalysis} className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark">
          Generate Analysis
        </button>
      </div>
    );
  }

  const d = editMode ? editState : analysis;

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <span className="font-semibold text-gray-900">AI Analysis</span>
          {saveSuccess && <span className="flex items-center gap-1 text-xs text-green-600"><Check className="h-3 w-3" /> Saved</span>}
        </div>
        <div className="flex gap-2">
          {!editMode ? (
            <>
              <button onClick={() => { setEditState(JSON.parse(JSON.stringify(analysis))); setEditMode(true); }} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50">
                <Edit2 className="h-3 w-3" /> Edit
              </button>
              <button onClick={handleDownload} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50">
                <Download className="h-3 w-3" /> Export
              </button>
              <button onClick={runAnalysis} disabled={loading} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-md text-xs hover:bg-primaryDark disabled:opacity-50">
                <Brain className="h-3 w-3" /> Re-run
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { setEditState(JSON.parse(JSON.stringify(analysis))); setEditMode(false); }} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50">
                <X className="h-3 w-3" /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-md text-xs hover:bg-primaryDark disabled:opacity-50">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">{error}</div>
      )}

      {/* Dataset summary */}
      {(d?.data_overview || d?.purpose) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">Dataset Summary</p>
              {editMode ? (
                <textarea value={editState?.data_overview || ''} onChange={e => setEditState(s => ({ ...s, data_overview: e.target.value }))} className="w-full text-sm text-blue-800 bg-white border border-blue-300 rounded p-2 resize-none" rows={3} />
              ) : (
                <p className="text-sm text-blue-800">{d.data_overview}</p>
              )}
              {d.purpose && <p className="text-xs text-blue-600 mt-2 italic">Purpose: {d.purpose}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Data quality */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-700 mb-3">Data Quality</p>
        <div className="flex flex-wrap gap-4">
          {(d?.completeness != null || d?.data_quality?.completeness_score) && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Completeness</p>
              <span className="px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded text-xs font-medium">
                {d.completeness != null ? `${d.completeness}%` : d.data_quality?.completeness_score}
              </span>
            </div>
          )}
          {(d?.data_quality_score != null || d?.data_quality?.overall_grade) && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Quality Grade</p>
              <span className="px-2 py-1 bg-primary/10 text-primary border border-primary/20 rounded text-xs font-medium">
                {d.data_quality_score != null ? `${d.data_quality_score}%` : d.data_quality?.overall_grade}
              </span>
            </div>
          )}
        </div>
        {d?.data_quality?.main_issues?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-yellow-500" /> Issues</p>
            <ul className="space-y-1">
              {d.data_quality.main_issues.map((issue, i) => (
                <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-yellow-500 flex-shrink-0" />
                  {issue}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(d?.recommendations?.length > 0 || d?.data_quality?.recommendations?.length > 0) && (
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-green-500" /> Recommendations</p>
            <ul className="space-y-1">
              {[...(d.recommendations || []), ...(d.data_quality?.recommendations || [])].map((rec, i) => (
                <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-green-500 flex-shrink-0" />
                  {typeof rec === 'object' ? rec.recommendation || JSON.stringify(rec) : rec}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Column details table */}
      {d?.column_details?.length > 0 && (
        <div>
          <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm font-semibold text-gray-900">Detailed Column Analysis</p>
          </div>
          <div className="overflow-x-auto rounded-lg border max-h-[500px]">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-primary text-white">
                <tr>
                  {['Column', 'Type', 'Criticality', 'Completeness', 'Business Significance', 'Issues', 'Recommendations'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-bold uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {d.column_details.map((col, i) => (
                  <tr key={i} className="hover:bg-blue-50 transition-colors">
                    <td className="px-3 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{col.column_name}</td>
                    <td className="px-3 py-2.5 text-sm"><span className="px-1.5 py-0.5 border border-gray-300 rounded text-xs">{col.data_type}</span></td>
                    <td className="px-3 py-2.5 text-sm">
                      {col.criticality && <span className={`px-1.5 py-0.5 rounded text-xs ${importanceBadge(col.criticality)}`}>{col.criticality}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600">
                      {col.completeness_percentage != null ? `${col.completeness_percentage.toFixed(1)}%` : '-'}
                      {col.null_count != null && <div className="text-[10px] text-gray-400">{col.null_count} nulls</div>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700 max-w-[180px]">{col.business_significance || '-'}</td>
                    <td className="px-3 py-2.5 text-xs text-amber-600">
                      {col.quality_issues?.length > 0 ? col.quality_issues.join('; ') : <span className="text-gray-400">None</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">
                      {col.recommendations?.length > 0 ? col.recommendations.map((r, ri) => <div key={ri}>• {r}</div>) : <span className="text-gray-400">None</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Key columns */}
      {d?.key_columns?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-900 mb-3">Key Columns</p>
          <div className="grid gap-2">
            {d.key_columns.map((col, i) => {
              const name = typeof col === 'object' ? (col.column_name || col.name) : col;
              const importance = typeof col === 'object' ? col.importance : null;
              const reason = typeof col === 'object' ? col.reason : null;
              return (
                <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 border border-gray-100">
                  <code className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs font-mono flex-shrink-0">{name}</code>
                  {importance && <span className={`px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 ${importanceBadge(importance)}`}>{importance}</span>}
                  {reason && <span className="text-xs text-gray-600">{reason}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Business insights */}
      {(d?.business_insights?.length > 0 || d?.potential_use_cases?.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-green-600" />
            <p className="text-sm font-medium text-gray-900">Business Insights</p>
          </div>
          <ul className="space-y-2">
            {d.business_insights.map((insight, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-gray-700">{typeof insight === 'object' ? JSON.stringify(insight) : insight}</span>
              </li>
            ))}
            {d.potential_use_cases.map((uc, i) => (
              <li key={`uc-${i}`} className="flex items-start gap-2">
                <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-gray-700">{typeof uc === 'object' ? JSON.stringify(uc) : uc}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Data relationships */}
      {d?.data_relationships && (
        d.data_relationships.potential_keys?.length > 0 ||
        d.data_relationships.categorical_columns?.length > 0 ||
        d.data_relationships.hierarchical_relationships?.length > 0
      ) && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Potential Keys', items: d.data_relationships.potential_keys, color: 'bg-gray-100 text-gray-700' },
            { label: 'Categorical Columns', items: d.data_relationships.categorical_columns, color: 'bg-primary/10 text-primary' },
            { label: 'Hierarchical Relationships', items: d.data_relationships.hierarchical_relationships, color: 'bg-green-50 text-green-700' },
          ].map(({ label, items, color }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-700 mb-2">{label}</p>
              <div className="flex flex-wrap gap-1">
                {items?.map((item, i) => (
                  <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] ${color}`}>{item}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {usage && (
        <p className="text-xs text-gray-400 text-right">
          {usage.model} · {usage.tokens_used?.toLocaleString()} tokens · ${usage.estimated_cost?.toFixed(4)}
        </p>
      )}

      <div className="flex justify-center pt-2">
        <button onClick={runAnalysis} disabled={loading || isGenerating} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
          Run Analysis Again
        </button>
      </div>
    </div>
  );
}
